/**
 * @file: auth.service.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { HttpClient } from '@euler/lib/request';
import { AuthenticatedUserInfo } from '@euler/model';
import {
  AuthenticatorNextActionApp,
  AuthenticatorResult,
  AuthProvider,
  TokenExchangeAuthProvider,
} from '@euler/model/auth';
import { ChangePasswordRequest } from '@euler/model/ChangePasswordRequest';
import { IdType } from '@euler/model/common';
import { ResetPasswordRequest } from '@euler/model/ResetPasswordRequest';
import { safeMarkDevFlag } from '@euler/utils';

const kUseShortLivedToken = safeMarkDevFlag(true);

export type QrcodeAuthChallengeResponse = {
  clientId: string;
  dataURI: string;
  ttl: number;
};

export class AuthService {
  constructor(private readonly api: HttpClient) {}

  async login<T extends AuthProvider>(
    authProviderData: T,
    options?: {
      // usually the app id selected by the user last time
      // need a way to persist the last selected app id and pass it
      // when login the next time
      defaultAppId?: IdType;
      // custom token expiry in seconds, non-production environment only
      tokenExpiresIn?: number;
    },
  ): Promise<AuthenticatorResult<T>> {
    return await this.api
      .post()
      .url('/auth/v2/login')
      .data({
        data: authProviderData,
        ...options,
        tokenExpiresIn: kUseShortLivedToken ? 10 * 60 : undefined,
      })
      .future();
  }

  async getApps(): Promise<AuthenticatorNextActionApp[]> {
    return await this.api.get().url('/auth/v2/apps').future();
  }

  async switchApp(
    appId: IdType,
  ): Promise<AuthenticatorResult<TokenExchangeAuthProvider>> {
    return await this.api
      .post()
      .url('/auth/v2/switch-app')
      .data({ appId })
      .future();
  }

  // throws error if user is not authenticated
  async getAuthenticatedUserInfo(): Promise<AuthenticatedUserInfo> {
    return await this.api.get().url('/auth/v2/user').future();
  }

  async refresh(refreshToken: string): Promise<{
    token: string;
    refreshToken: string;
    refreshTokenExpiresIn: number;
  }> {
    return await this.api
      .post()
      .url('/auth/v2/refresh')
      .data({ refreshToken })
      .future();
  }

  async changePassword(params: ChangePasswordRequest) {
    return await this.api.put().url('/auth/password').data(params).future();
  }

  async resetPassword(params: ResetPasswordRequest) {
    return await this.api
      .post()
      .url('/auth/password/resets')
      .data(params)
      .future();
  }

  async beginQrcodeAuthChallenge(
    deviceName: string,
    options?: {
      size?: number;
      margin?: number;
    },
  ): Promise<QrcodeAuthChallengeResponse> {
    return await this.api
      .post()
      .url('/auth/qrcode/challenge', { ...options })
      .data({ deviceName })
      .future();
  }
}
